// src/pages/ManageAccess.jsx
// Merged access management page: Directory Access + Offer Letter Access

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import {
  collection, onSnapshot, query, where, doc,
  setDoc, deleteDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  FaUserCheck, FaPlus, FaTrash, FaSearch, FaShieldAlt,
  FaUserShield, FaToggleOn, FaToggleOff, FaFileContract,
  FaCheckCircle, FaTimesCircle, FaSync, FaUserCog, FaIdCard, FaToolbox
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import styles from './ManageAccess.module.css';

const ManageAccess = () => {
  const { userData } = useAuth();
  const [activeTab, setActiveTab] = useState('directory');

  // ── Directory Access state ──────────────────────────────
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [dirLoading, setDirLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // ── Offer Letter Access state ───────────────────────────
  const [admins, setAdmins] = useState([]);
  const [offerLoading, setOfferLoading] = useState(true);
  const [adminSearch, setAdminSearch] = useState('');
  const [updating, setUpdating] = useState(null);

  // ── ID Card Access state ──────────────────────────────────
  const [idCardAdmins, setIdCardAdmins] = useState([]);
  const [idCardLoading, setIdCardLoading] = useState(true);
  const [idCardSearch, setIdCardSearch] = useState('');
  const [idCardUpdating, setIdCardUpdating] = useState(null);
  // ── HR Tools Access state ───────────────────────────────
  const [hrToolsAdmins, setHrToolsAdmins] = useState([]);
  const [hrToolsLoading, setHrToolsLoading] = useState(true);
  const [hrToolsSearch, setHrToolsSearch] = useState('');
  const [hrToolsUpdating, setHrToolsUpdating] = useState(null);
  // ── Directory functions ─────────────────────────────────


  const handleAddViewer = async (user) => {
    try {
      await setDoc(doc(db, 'authorized_viewers', user.uid), {
        uid: user.uid,
        email: user.email,
        name: user.name,
        employeeId: user.employeeId,
        role: user.role || 'employee',
        grantedBy: userData.uid,
        grantedByName: userData.name,
        grantedAt: serverTimestamp()
      });
      toast.success(`${user.name} added to authorized viewers!`);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding viewer:', error);
      toast.error('Failed to add viewer');
    }
  };

  const handleRemoveViewer = async (viewerId, viewerName) => {
    if (!window.confirm(`Remove ${viewerName} from authorized viewers?`)) return;
    try {
      await deleteDoc(doc(db, 'authorized_viewers', viewerId));
      toast.success(`${viewerName} removed from authorized viewers`);
    } catch (error) {
      console.error('Error removing viewer:', error);
      toast.error('Failed to remove viewer');
    }
  };

  const isAuthorized = (uid) => authorizedUsers.some(v => v.uid === uid);

  // Role → CSS module class lookup for modal employee list
  const roleStyles = {
    superadmin: styles.avmRoleSuperadmin,
    admin: styles.avmRoleAdmin,
    employee: styles.avmRoleEmployee,
  };

  const availableEmployees = allEmployees.filter(emp =>
    !isAuthorized(emp.uid) &&
    (emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     emp.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleAccess = async (uid, current) => {
    setUpdating(uid);
    try {
      await updateDoc(doc(db, 'users', uid), { offerLetterAccess: !current });
      setAdmins(prev =>
        prev.map(a => a.uid === uid ? { ...a, offerLetterAccess: !current } : a)
      );
      toast.success(!current ? 'Offer letter access granted!' : 'Offer letter access revoked.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update access.');
    }
    setUpdating(null);
  };

  const toggleIdCardAccess = async (uid, current) => {
    setIdCardUpdating(uid);
    try {
      await updateDoc(doc(db, 'users', uid), { idCardAccess: !current });
      setIdCardAdmins(prev =>
        prev.map(a => a.uid === uid ? { ...a, idCardAccess: !current } : a)
      );
      toast.success(!current ? 'ID card access granted!' : 'ID card access revoked.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update access.');
    }
    setIdCardUpdating(null);
  };

  const toggleHrToolsAccess = async (uid, current) => {
    setHrToolsUpdating(uid);
    try {
      await updateDoc(doc(db, 'users', uid), { hrToolsAccess: !current });
      setHrToolsAdmins(prev =>
        prev.map(a => a.uid === uid ? { ...a, hrToolsAccess: !current } : a)
      );
      toast.success(!current ? 'HR tools access granted!' : 'HR tools access revoked.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update access.');
    }
    setHrToolsUpdating(null);
  };

  const filteredAdmins = admins.filter(a =>
    `${a.name || ''} ${a.email || ''}`.toLowerCase().includes(adminSearch.toLowerCase())
  );

  const filteredIdCardAdmins = idCardAdmins.filter(a =>
    `${a.name || ''} ${a.email || ''}`.toLowerCase().includes(idCardSearch.toLowerCase())
  );

  const filteredHrToolsAdmins = hrToolsAdmins.filter(a =>
    `${a.name || ''} ${a.email || ''}`.toLowerCase().includes(hrToolsSearch.toLowerCase())
  );

  const grantedCount = admins.filter(a => a.offerLetterAccess).length;
  const idCardGrantedCount = idCardAdmins.filter(a => a.idCardAccess).length;
  const hrToolsGrantedCount = hrToolsAdmins.filter(a => a.hrToolsAccess).length;

  // ── Real-time listeners ─────────────────────────────────
  useEffect(() => {
    setDirLoading(true);
    setOfferLoading(true);
    setIdCardLoading(true);
    setHrToolsLoading(true);
    const unsub1 = onSnapshot(
      collection(db, 'authorized_viewers'),
      (snap) => { setAuthorizedUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setDirLoading(false); },
      (err) => { console.error(err); toast.error('Failed to load authorized viewers'); setDirLoading(false); }
    );
    const unsub2 = onSnapshot(
      collection(db, 'users'),
      (snap) => { setAllEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
      (err) => { console.error('Error fetching employees:', err); }
    );
    const adminsQ = query(collection(db, 'users'), where('role', '==', 'admin'));
    const unsub3 = onSnapshot(
      adminsQ,
      (snap) => { setAdmins(snap.docs.map(d => ({ uid: d.id, ...d.data() }))); setOfferLoading(false); },
      (err) => { console.error(err); toast.error('Failed to load admin list.'); setOfferLoading(false); }
    );
    const adminsQ2 = query(collection(db, 'users'), where('role', '==', 'admin'));
    const unsub4 = onSnapshot(
      adminsQ2,
      (snap) => { setIdCardAdmins(snap.docs.map(d => ({ uid: d.id, ...d.data() }))); setIdCardLoading(false); },
      (err) => { console.error(err); toast.error('Failed to load admin list.'); setIdCardLoading(false); }
    );
    const adminsQ3 = query(collection(db, 'users'), where('role', '==', 'admin'));
    const unsub5 = onSnapshot(
      adminsQ3,
      (snap) => { setHrToolsAdmins(snap.docs.map(d => ({ uid: d.id, ...d.data() }))); setHrToolsLoading(false); },
      (err) => { console.error(err); toast.error('Failed to load admin list.'); setHrToolsLoading(false); }
    );
    return () => { unsub1(); unsub2(); unsub3(); unsub4(); unsub5(); };
  }, []);

  // ── Render ──────────────────────────────────────────────

  return (
    <div className={styles.manageAccessPage}>

      {/* ── Segmented toggle tab bar ── */}
      <div className={styles.macTabBar}>
        <div className={styles.macTabGroup}>
          <button
            className={`${styles.macTab} ${activeTab === 'directory' ? styles.macTabActive : ''}`}
            onClick={() => setActiveTab('directory')}
          >
            <FaShieldAlt /> Employee Directory Access
          </button>
          <button
            className={`${styles.macTab} ${activeTab === 'offer' ? styles.macTabActive : ''}`}
            onClick={() => setActiveTab('offer')}
          >
            <FaUserShield /> Offer Letter Access
          </button>
          <button
            className={`${styles.macTab} ${activeTab === 'idcard' ? styles.macTabActive : ''}`}
            onClick={() => setActiveTab('idcard')}
          >
            <FaIdCard /> ID Card Access
          </button>
          <button
            className={`${styles.macTab} ${activeTab === 'hrtools' ? styles.macTabActive : ''}`}
            onClick={() => setActiveTab('hrtools')}
          >
            <FaToolbox /> HR Tools Access
          </button>
        </div>
      </div>

      {/* ══ Directory Access Tab ══════════════════════════ */}
      {activeTab === 'directory' && (
        <div className={styles.manageViewersContainer}>
          {dirLoading ? (
            <div className={styles.dirLoadingState}>
              <div className={styles.dirSpinner} />
              <p>Loading authorized viewers...</p>
            </div>
          ) : (
            <>
              {/* Gradient header card */}
              <div className={styles.dirHeader}>
                <div className={styles.dirHeaderLeft}>
                  <FaShieldAlt className={styles.dirHeaderIcon} />
                  <div>
                    <h1>Manage Employee Directory Access</h1>
                    <p>Control who can view the Employee Directory</p>
                  </div>
                </div>
                <button className={styles.dirGrantBtn} onClick={() => setShowAddModal(true)}>
                  <FaPlus /> Grant Access
                </button>
              </div>

              {/* Stat cards */}
              <div className={styles.dirStatsRow}>
                <div className={`${styles.dirStatCard} ${styles.dirStatGreen}`}>
                  <FaUserCheck className={styles.dirStatIcon} />
                  <div>
                    <p className={styles.dirStatValue}>{authorizedUsers.length}</p>
                    <p className={styles.dirStatLabel}>Authorized Users</p>
                  </div>
                </div>
                <div className={`${styles.dirStatCard} ${styles.dirStatBlue}`}>
                  <FaPlus className={styles.dirStatIcon} />
                  <div>
                    <p className={styles.dirStatValue}>{allEmployees.length - authorizedUsers.length}</p>
                    <p className={styles.dirStatLabel}>Available to Add</p>
                  </div>
                </div>
              </div>

              {/* Users table */}
              <div className={styles.viewersList}>
                <div className={styles.viewersListHeader}>
                  <h3>Currently Authorized Users</h3>
                  <span className={styles.dirUserCount}>{authorizedUsers.length} user{authorizedUsers.length !== 1 ? 's' : ''}</span>
                </div>
                {authorizedUsers.length === 0 ? (
                  <div className={styles.dirEmpty}>
                    <FaUserCheck />
                    <p>No authorized viewers yet. Click "Grant Access" to add someone.</p>
                  </div>
                ) : (
                  <table className={styles.dirTable}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Employee ID</th>
                        <th>Email</th>
                        <th>Granted By</th>
                        <th>Granted On</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {authorizedUsers.map(viewer => (
                        <tr key={viewer.id}>
                          <td className={styles.dirNameCell}>{viewer.name}</td>
                          <td><span className={styles.dirEmpidBadge}>{viewer.employeeId}</span></td>
                          <td>{viewer.email}</td>
                          <td>{viewer.grantedByName || 'System'}</td>
                          <td className={styles.dirDateCell}>
                            {viewer.grantedAt?.toDate?.()?.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) || 'N/A'}
                          </td>
                          <td>
                            {(viewer.role === 'superadmin' || viewer.email === 'prepxpathshala@gmail.com') ? (
                              <span className={styles.avmProtectedBadge} title="Superadmin access cannot be revoked">🔒 Protected</span>
                            ) : (
                              <button
                                className={styles.actionBtnDanger}
                                onClick={() => handleRemoveViewer(viewer.id, viewer.name)}
                              >
                                <FaTrash /> Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Grant Access Modal */}
              {showAddModal && (
                <div className={styles.modalBackdrop} onClick={() => { setShowAddModal(false); setSearchTerm(''); }}>
                  <div className={styles.addViewerModal} onClick={e => e.stopPropagation()}>
                    <div className={styles.avmHeader}>
                      <div className={styles.avmHeaderText}>
                        <FaShieldAlt />
                        <div>
                          <h2>Grant Employee Directory Access</h2>
                          <p>Select an employee to allow them to view the Employee Directory</p>
                        </div>
                      </div>
                      <button className={styles.avmCloseBtn} onClick={() => { setShowAddModal(false); setSearchTerm(''); }}>×</button>
                    </div>

                    <div className={styles.avmSearch}>
                      <FaSearch className={styles.avmSearchIcon} />
                      <input
                        type="text"
                        className={styles.avmSearchInput}
                        placeholder="Search by name, employee ID or email…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>

                    <div className={styles.avmList}>
                      {availableEmployees.length === 0 ? (
                        <div className={styles.avmEmpty}>
                          <FaUserCheck size={32} />
                          <p>{searchTerm ? 'No employees match your search.' : 'All employees already have access.'}</p>
                        </div>
                      ) : (
                        availableEmployees.map(emp => (
                          <div key={emp.id} className={styles.avmCard}>
                            <img
                              src={emp.photoURL || '/default-avatar.png'}
                              alt={emp.name}
                              className={styles.avmAvatar}
                            />
                            <div className={styles.avmCardInfo}>
                              <strong>{emp.name || '—'}</strong>
                              <span>{emp.employeeId && <>{emp.employeeId} &bull; </>}{emp.email}</span>
                              <span className={`${styles.avmRoleBadge} ${roleStyles[emp.role] || ''}`}>{emp.role}</span>
                            </div>
                            <button className={styles.avmGrantBtn} onClick={() => handleAddViewer(emp)}>
                              <FaPlus /> Grant
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ Offer Letter Access Tab ═══════════════════════ */}
      {activeTab === 'offer' && (
        <motion.div
          className={styles.container}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <FaUserShield className={styles.headerIcon} />
              <div>
                <h1 className={styles.title}>Manage Offer Letter Access</h1>
                <p className={styles.subtitle}>
                  Control which admins can generate and export offer letters
                </p>
              </div>
            </div>
            <button className={styles.refreshBtn} onClick={() => {}}>
              <FaSync className={offerLoading ? styles.spin : ''} /> Refresh
            </button>
          </div>

          {/* Stats row */}
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <FaUserCog className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{admins.length}</p>
                <p className={styles.statLabel}>Total Admins</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statGreen}`}>
              <FaCheckCircle className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{grantedCount}</p>
                <p className={styles.statLabel}>Access Granted</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statRed}`}>
              <FaTimesCircle className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{admins.length - grantedCount}</p>
                <p className={styles.statLabel}>Access Denied</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statBlue}`}>
              <FaFileContract className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>Superadmin</p>
                <p className={styles.statLabel}>Always has access</p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className={styles.searchRow}>
            <FaSearch className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search admins by name or email..."
              value={adminSearch}
              onChange={e => setAdminSearch(e.target.value)}
            />
          </div>

          {/* Admin list */}
          {offerLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading admins...</p>
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className={styles.emptyState}>
              <FaUserShield className={styles.emptyIcon} />
              <p>{adminSearch ? 'No admins match your search.' : 'No admin accounts found.'}</p>
            </div>
          ) : (
            <div className={styles.adminGrid}>
              <AnimatePresence>
                {filteredAdmins.map((admin, i) => (
                  <motion.div
                    key={admin.uid}
                    className={`${styles.adminCard} ${admin.offerLetterAccess ? styles.cardGranted : ''}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <div className={styles.avatar}>
                      {admin.photoURL ? (
                        <img src={admin.photoURL} alt={admin.name} className={styles.avatarImg} />
                      ) : (
                        <span className={styles.avatarFallback}>
                          {(admin.name || admin.email || 'A')[0].toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div className={styles.adminInfo}>
                      <p className={styles.adminName}>{admin.name || 'Unnamed Admin'}</p>
                      <p className={styles.adminEmail}>{admin.email}</p>
                      {admin.department && (
                        <p className={styles.adminDept}>{admin.department}</p>
                      )}
                    </div>

                    <div className={styles.accessBadge}>
                      {admin.offerLetterAccess ? (
                        <span className={styles.badgeGranted}>
                          <FaCheckCircle /> Granted
                        </span>
                      ) : (
                        <span className={styles.badgeDenied}>
                          <FaTimesCircle /> No Access
                        </span>
                      )}
                    </div>

                    <button
                      className={`${styles.toggleBtn} ${admin.offerLetterAccess ? styles.toggleOn : styles.toggleOff}`}
                      onClick={() => toggleAccess(admin.uid, !!admin.offerLetterAccess)}
                      disabled={updating === admin.uid}
                      title={admin.offerLetterAccess ? 'Revoke access' : 'Grant access'}
                    >
                      {updating === admin.uid ? (
                        <span className={styles.spinner} />
                      ) : admin.offerLetterAccess ? (
                        <><FaToggleOn className={styles.toggleIcon} /> Revoke</>
                      ) : (
                        <><FaToggleOff className={styles.toggleIcon} /> Grant</>
                      )}
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Info box */}
          <div className={styles.infoBox}>
            <FaFileContract className={styles.infoIcon} />
            <p>
              Admins with access can open the Offer Letter Editor, compose letters, and download them as DOCX or PDF.
              Superadmin always has access regardless of this setting.
            </p>
          </div>
        </motion.div>
      )}

      {/* ══ ID Card Access Tab ════════════════════════════ */}
      {activeTab === 'idcard' && (
        <motion.div
          className={styles.container}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <FaIdCard className={styles.headerIcon} />
              <div>
                <h1 className={styles.title}>Manage ID Card Access</h1>
                <p className={styles.subtitle}>
                  Control which admins can generate and download employee ID cards
                </p>
              </div>
            </div>
            <button className={styles.refreshBtn} onClick={() => {}}>
              <FaSync className={idCardLoading ? styles.spin : ''} /> Refresh
            </button>
          </div>

          {/* Stats row */}
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <FaUserCog className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{idCardAdmins.length}</p>
                <p className={styles.statLabel}>Total Admins</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statGreen}`}>
              <FaCheckCircle className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{idCardGrantedCount}</p>
                <p className={styles.statLabel}>Access Granted</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statRed}`}>
              <FaTimesCircle className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{idCardAdmins.length - idCardGrantedCount}</p>
                <p className={styles.statLabel}>Access Denied</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statBlue}`}>
              <FaIdCard className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>Superadmin</p>
                <p className={styles.statLabel}>Always has access</p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className={styles.searchRow}>
            <FaSearch className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search admins by name or email..."
              value={idCardSearch}
              onChange={e => setIdCardSearch(e.target.value)}
            />
          </div>

          {/* Admin list */}
          {idCardLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading admins...</p>
            </div>
          ) : filteredIdCardAdmins.length === 0 ? (
            <div className={styles.emptyState}>
              <FaIdCard className={styles.emptyIcon} />
              <p>{idCardSearch ? 'No admins match your search.' : 'No admin accounts found.'}</p>
            </div>
          ) : (
            <div className={styles.adminGrid}>
              <AnimatePresence>
                {filteredIdCardAdmins.map((admin, i) => (
                  <motion.div
                    key={admin.uid}
                    className={`${styles.adminCard} ${admin.idCardAccess ? styles.cardGranted : ''}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <div className={styles.avatar}>
                      {admin.photoURL ? (
                        <img src={admin.photoURL} alt={admin.name} className={styles.avatarImg} />
                      ) : (
                        <span className={styles.avatarFallback}>
                          {(admin.name || admin.email || 'A')[0].toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div className={styles.adminInfo}>
                      <p className={styles.adminName}>{admin.name || 'Unnamed Admin'}</p>
                      <p className={styles.adminEmail}>{admin.email}</p>
                      {admin.department && (
                        <p className={styles.adminDept}>{admin.department}</p>
                      )}
                    </div>

                    <div className={styles.accessBadge}>
                      {admin.idCardAccess ? (
                        <span className={styles.badgeGranted}>
                          <FaCheckCircle /> Granted
                        </span>
                      ) : (
                        <span className={styles.badgeDenied}>
                          <FaTimesCircle /> No Access
                        </span>
                      )}
                    </div>

                    <button
                      className={`${styles.toggleBtn} ${admin.idCardAccess ? styles.toggleOn : styles.toggleOff}`}
                      onClick={() => toggleIdCardAccess(admin.uid, !!admin.idCardAccess)}
                      disabled={idCardUpdating === admin.uid}
                      title={admin.idCardAccess ? 'Revoke access' : 'Grant access'}
                    >
                      {idCardUpdating === admin.uid ? (
                        <span className={styles.spinner} />
                      ) : admin.idCardAccess ? (
                        <><FaToggleOn className={styles.toggleIcon} /> Revoke</>
                      ) : (
                        <><FaToggleOff className={styles.toggleIcon} /> Grant</>
                      )}
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Info box */}
          <div className={styles.infoBox}>
            <FaIdCard className={styles.infoIcon} />
            <p>
              Admins with access can open the ID Card Generator, fill in employee details,
              and download 4K print-ready PNG ID cards.
              Superadmin always has access regardless of this setting.
            </p>
          </div>
        </motion.div>
      )}

      {/* ══ HR Tools Access Tab ═══════════════════════════ */}
      {activeTab === 'hrtools' && (
        <motion.div
          className={styles.container}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <FaToolbox className={styles.headerIcon} />
              <div>
                <h1 className={styles.title}>Manage HR Tools Access</h1>
                <p className={styles.subtitle}>
                  Control which admins can access the HR Tools page
                </p>
              </div>
            </div>
            <button className={styles.refreshBtn} onClick={() => {}}>
              <FaSync className={hrToolsLoading ? styles.spin : ''} /> Refresh
            </button>
          </div>

          {/* Stats row */}
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <FaUserCog className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{hrToolsAdmins.length}</p>
                <p className={styles.statLabel}>Total Admins</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statGreen}`}>
              <FaCheckCircle className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{hrToolsGrantedCount}</p>
                <p className={styles.statLabel}>Access Granted</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statRed}`}>
              <FaTimesCircle className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>{hrToolsAdmins.length - hrToolsGrantedCount}</p>
                <p className={styles.statLabel}>Access Denied</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statBlue}`}>
              <FaToolbox className={styles.statIcon} />
              <div>
                <p className={styles.statValue}>Superadmin</p>
                <p className={styles.statLabel}>Always has access</p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className={styles.searchRow}>
            <FaSearch className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search admins by name or email..."
              value={hrToolsSearch}
              onChange={e => setHrToolsSearch(e.target.value)}
            />
          </div>

          {/* Admin list */}
          {hrToolsLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading admins...</p>
            </div>
          ) : filteredHrToolsAdmins.length === 0 ? (
            <div className={styles.emptyState}>
              <FaToolbox className={styles.emptyIcon} />
              <p>{hrToolsSearch ? 'No admins match your search.' : 'No admin accounts found.'}</p>
            </div>
          ) : (
            <div className={styles.adminGrid}>
              <AnimatePresence>
                {filteredHrToolsAdmins.map((admin, i) => (
                  <motion.div
                    key={admin.uid}
                    className={`${styles.adminCard} ${admin.hrToolsAccess ? styles.cardGranted : ''}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <div className={styles.avatar}>
                      {admin.photoURL ? (
                        <img src={admin.photoURL} alt={admin.name} className={styles.avatarImg} />
                      ) : (
                        <span className={styles.avatarFallback}>
                          {(admin.name || admin.email || 'A')[0].toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div className={styles.adminInfo}>
                      <p className={styles.adminName}>{admin.name || 'Unnamed Admin'}</p>
                      <p className={styles.adminEmail}>{admin.email}</p>
                      {admin.department && (
                        <p className={styles.adminDept}>{admin.department}</p>
                      )}
                    </div>

                    <div className={styles.accessBadge}>
                      {admin.hrToolsAccess ? (
                        <span className={styles.badgeGranted}>
                          <FaCheckCircle /> Granted
                        </span>
                      ) : (
                        <span className={styles.badgeDenied}>
                          <FaTimesCircle /> No Access
                        </span>
                      )}
                    </div>

                    <button
                      className={`${styles.toggleBtn} ${admin.hrToolsAccess ? styles.toggleOn : styles.toggleOff}`}
                      onClick={() => toggleHrToolsAccess(admin.uid, !!admin.hrToolsAccess)}
                      disabled={hrToolsUpdating === admin.uid}
                      title={admin.hrToolsAccess ? 'Revoke access' : 'Grant access'}
                    >
                      {hrToolsUpdating === admin.uid ? (
                        <span className={styles.spinner} />
                      ) : admin.hrToolsAccess ? (
                        <><FaToggleOn className={styles.toggleIcon} /> Revoke</>
                      ) : (
                        <><FaToggleOff className={styles.toggleIcon} /> Grant</>
                      )}
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Info box */}
          <div className={styles.infoBox}>
            <FaToolbox className={styles.infoIcon} />
            <p>
              Admins with access can open HR Tools and manage important links,
              standard email templates, and subscription renewals.
              Superadmin always has access regardless of this setting.
            </p>
          </div>
        </motion.div>
      )}

    </div>
  );
};

export default ManageAccess;
